import { resamplingRefusal } from '~/domain/patient/resamplingOrigin';
import type { ImageId } from '../../changes/ids';
import type { PendingDatasetChanges } from '../../changes/PendingChangesStore';
import type { DatasetDeletionGateway } from '../../persistence/RegistrationPersistenceGateway';
import { domainTrace } from '../../diagnostics/domainTrace';
import type { Patient } from '../../patient/patient';
import type {
    ImagePersistenceGateway,
    PersistedImageSeries,
} from '../../persistence/ImagePersistenceGateway';
import { MAX_UPLOAD_REQUEST_BYTES } from '../../persistence/ImagePersistenceGateway';
import type {
    ConnectorVersionToken,
    PatientPersistenceRef,
} from '../../persistence/PersistenceRef';
import type { DatasetSaver, SaveScope } from '../save/DatasetSaver';
import { deleteDatasetRow, inScope, mapSaveErrors } from '../save/DatasetSaver';
import type { SaveEffect, SaveEntryResult } from '../save/SaveEffect';
import type { SaveOutcome } from '../save/SaveOutcome';
import type { SealedImage, SealedSaveIntent } from '../save/SealedSaveIntent';
import type { PersistenceRefReader } from '../../persistence/PersistenceRegistry';

const OVER_LIMIT_MESSAGE =
    'The image series exceeds the 512 MB upload limit and ' +
    'cannot be saved to the patient database. Export it to ' +
    'disk instead.';

/**
 * Lower-bound size of one slice without serializing it: the PixelData
 * payload dominates a DICOM file, and its byteLength is readable for free
 * from the naturalized dataset. Headers only add, so an estimate already
 * over the limit is a certain refusal — taken BEFORE materializing
 * hundreds of megabytes whose only fate is to be refused exactly.
 */
function pixelDataByteLength(dataset: unknown): number {
    const pixelData = (dataset as { PixelData?: unknown } | null | undefined)
        ?.PixelData;
    if (!pixelData) {
        return 0;
    }
    if (pixelData instanceof ArrayBuffer || ArrayBuffer.isView(pixelData)) {
        return pixelData.byteLength;
    }
    if (Array.isArray(pixelData)) {
        return pixelData.reduce(
            (sum: number, part) =>
                sum + pixelDataByteLength({ PixelData: part }),
            0
        );
    }
    return 0;
}

/**
 * Persists pending image-series changes to the connector database. Driven
 * by the {@link PatientSaveService} orchestrator; entries are processed in
 * stable id order, each independently. Deletions run through this saver
 * too, in the orchestrator's leaf-last 'deletes' stage (see saveEntries).
 *
 * - New (a locally created series, e.g. a reformat/crop result): the
 *   series' slices upload through the standard DICOM ingest as ONE atomic
 *   request — a failed request leaves nothing behind on the server, and a
 *   retried one is idempotent (already-present SOP Instance UIDs are
 *   skipped). The ingest routes purely by the DICOM PatientID inside the
 *   files, so the slices are verified against the loaded patient BEFORE
 *   anything is sent. The ingest response carries the rows the batch
 *   touched, so identity is adopted from the SAME request that persisted
 *   the series; a response without rows falls back to the patient detail.
 *   When neither yields the row, the entry fails WITHOUT a ref and the
 *   next save re-runs the idempotent upload.
 *   A row the request did NOT create (an idempotent retry into a row that
 *   predates it) is adopted but never written to with its live version
 *   token: a diverged display name is a conflict, the ref is stored with
 *   an unacknowledged marker, and later saves re-judge the row's current
 *   state — only the user's explicit overwrite writes (see
 *   settleAdoptedRow).
 * - Modified (label rename): version-guarded PATCH of the row's series
 *   description; the stored slice files are untouched by design.
 */
export class ImageSaveService implements DatasetSaver {
    readonly entity = 'image' as const;

    constructor(
        private readonly refs: PersistenceRefReader,
        private readonly gateway: ImagePersistenceGateway,
        private readonly datasetDeletion: DatasetDeletionGateway
    ) {}

    /**
     * Captures everything the image saver transmits, SYNCHRONOUSLY, in the
     * orchestrator's no-await window. Serialization only happens for a
     * first-attempt upload (a `new` entry with no ref) — a rename or a
     * retry whose row already exists transmits no bytes and seals none —
     * and an over-limit series is refused on the cheap PixelData estimate
     * BEFORE any bytes are materialized.
     */
    seal(
        patient: Patient,
        entries: readonly PendingDatasetChanges[]
    ): ReadonlyMap<ImageId, SealedImage> {
        const sealed = new Map<ImageId, SealedImage>();
        for (const entry of entries) {
            if (entry.kind !== 'image' || entry.status === 'deleted') {
                continue;
            }
            const id = entry.id as ImageId;
            const image =
                patient.images.find((candidate) => candidate.id === id) ?? null;
            if (!image) {
                continue; // Reported as failed in the per-entry pass.
            }
            const base = {
                slicePatientIds: image.imageSlices.map(
                    (slice) => slice.datasetWrapper.PatientID
                ),
                seriesUid: image.seriesUid,
                label: image.seriesMetadata.seriesDescription ?? '',
            };
            const uploads =
                entry.status === 'new' && this.refs.getRef(id) === null;
            if (!uploads) {
                sealed.set(id, { ...base, blobs: null });
                continue;
            }

            const estimate = image.imageSlices.reduce(
                (sum, slice) =>
                    sum + pixelDataByteLength(slice.datasetWrapper.dataset),
                0
            );
            if (estimate > MAX_UPLOAD_REQUEST_BYTES) {
                sealed.set(id, {
                    ...base,
                    blobs: null,
                    sealFailure: OVER_LIMIT_MESSAGE,
                });
                continue;
            }

            // A derived series stored under this patient would pass for an acquired one.
            if (image.resamplingOrigin) {
                sealed.set(id, {
                    ...base,
                    blobs: null,
                    sealFailure: `${resamplingRefusal(
                        image.resamplingOrigin
                    )} It cannot be saved to the patient database.`,
                });
                continue;
            }

            // A series that is not all there is not the series it claims to
            // be. Uploading one would publish a truncated volume under the
            // source's identity, where nothing downstream marks it short.
            // `getSlicesAsDicomBlobs` below refuses it by throwing; sealing
            // the reason instead keeps it a refusal the user can read, in the
            // same shape as the over-limit one (SR-WOR-H-37-3).
            if (!image.isComplete) {
                sealed.set(id, {
                    ...base,
                    blobs: null,
                    sealFailure: image.isIncompleteFinal
                        ? `The image series is incomplete — ${image.unavailableSliceCount} of ` +
                          `${image.expectedSliceCount} slices could not be retrieved. ` +
                          'An incomplete series cannot be saved to the patient database.'
                        : `The image series is still loading (${image.loadedSliceCount} of ` +
                          `${image.expectedSliceCount} slices). Wait for it to finish, then save.`,
                });
                continue;
            }

            const blobs = [...image.getSlicesAsDicomBlobs()];
            if (blobs.length === 0) {
                sealed.set(id, {
                    ...base,
                    blobs: null,
                    sealFailure: 'The image series has no slices to save.',
                });
                continue;
            }
            let totalBytes = 0;
            for (const blob of blobs) {
                totalBytes += blob.size;
            }
            if (totalBytes > MAX_UPLOAD_REQUEST_BYTES) {
                // Honest refusal: chunking would break the all-or-nothing
                // upload (a half-uploaded series looks complete to other
                // sessions). Export to disk remains available. The exact
                // check backs the estimate — headers only add bytes.
                sealed.set(id, {
                    ...base,
                    blobs: null,
                    sealFailure: OVER_LIMIT_MESSAGE,
                });
                continue;
            }
            sealed.set(id, { ...base, blobs });
        }
        return sealed;
    }

    async saveEntries(
        sealed: SealedSaveIntent,
        patientRef: PatientPersistenceRef,
        overwrite: ReadonlySet<string> | null,
        scope: SaveScope = 'all'
    ): Promise<SaveEntryResult[]> {
        // The orchestrator runs this saver twice: creates/renames before
        // the structure-set stage (a new set's skeleton references its
        // image series row) and deletes leaf-last, once nothing that may
        // reference the series is left. The scope names which lane.
        const ordered = [...sealed.intent.state.byImage.values()]
            .filter((entry) => inScope(entry.status, scope))
            .sort((a, b) => a.id.localeCompare(b.id));
        const results: SaveEntryResult[] = [];
        for (const entry of ordered) {
            results.push(
                await this.saveOne(
                    entry,
                    patientRef,
                    overwrite?.has(entry.id) ?? false,
                    sealed.images.get(entry.id as ImageId) ?? null
                )
            );
        }
        return results;
    }

    private async saveOne(
        entry: PendingDatasetChanges,
        patientRef: PatientPersistenceRef,
        overwrite: boolean,
        sealed: SealedImage | null
    ): Promise<SaveEntryResult> {
        const id = entry.id as ImageId;
        domainTrace.service('ImageSaveService.saveOne', {
            id,
            status: entry.status,
        });
        // Owned OUTSIDE the error mapping: an upload that committed and
        // adopted its row before a later step threw has already earned
        // its setRef, and it must reach the orchestrator with the failed
        // outcome or the retry loses track of the row.
        const effects: SaveEffect[] = [];
        const outcome = await mapSaveErrors(this.entity, id, async () => {
            if (entry.status === 'deleted') {
                // Nothing sealed for a deletion — there is nothing to
                // transmit but the dataset id the registry holds.
                return deleteDatasetRow(
                    this.refs,
                    this.entity,
                    id,
                    (ref) => this.datasetDeletion.deleteImage(ref),
                    effects
                );
            }
            // The seal ran in the no-await window; a missing entry means the
            // image was already gone from the session when the save began.
            // There is no live fallback by design.
            if (!sealed) {
                return {
                    kind: 'failed',
                    entity: 'image',
                    id,
                    message: 'The image no longer exists in this session.',
                };
            }
            // A refusal decided at seal time (over-limit, empty) is this
            // entry's honest outcome; nothing touches the wire for it.
            if (sealed.sealFailure) {
                return {
                    kind: 'failed',
                    entity: 'image',
                    id,
                    message: sealed.sealFailure,
                };
            }
            if (entry.status === 'new') {
                return await this.saveNew(
                    id,
                    patientRef,
                    overwrite,
                    sealed,
                    effects
                );
            }
            return await this.saveModified(
                id,
                patientRef,
                overwrite,
                sealed,
                effects
            );
        });
        return { outcome, effects };
    }

    private async saveNew(
        id: ImageId,
        patientRef: PatientPersistenceRef,
        overwrite: boolean,
        sealed: SealedImage,
        effects: SaveEffect[]
    ): Promise<SaveOutcome> {
        const existingRef = this.refs.getRef(id);
        if (existingRef !== null) {
            // A previous attempt uploaded AND adopted identity but the
            // outcome was lost (e.g. the report never completed). The
            // series is in the database; re-uploading identical bytes
            // would be wasted work — but a rename made since then still
            // has to reach the row (see syncAdoptedLabel).
            const rows = await this.gateway
                .listPatientImageSeries(patientRef.patientId)
                .catch(() => null);
            if (!rows) {
                // Reporting 'saved' here would be a save without evidence: a
                // transient list failure would clear the entry, completeSave
                // would re-baseline the label, and an outstanding rename would
                // be lost for good — the exact failure this branch exists to
                // prevent. Keep it pending; the retry is idempotent.
                return {
                    kind: 'failed',
                    entity: 'image',
                    id,
                    message:
                        'The image series could not be confirmed in the ' +
                        'patient database. Save again to retry.',
                };
            }
            const current = rows.find(
                (candidate) => candidate.datasetId === existingRef.datasetId
            );
            if (!current) {
                // The row we hold a ref to is gone — another session deleted
                // it. The series is NOT saved, and saying so is the only
                // honest answer.
                return {
                    kind: 'failed',
                    entity: 'image',
                    id,
                    message:
                        'The image series no longer exists in the patient ' +
                        'database. It may have been deleted in another session.',
                };
            }
            if (existingRef.unacknowledgedVersion) {
                // The held token was ADOPTED, never acknowledged — writing
                // with it (or with the equally live listed one) would
                // silently replace another session's rename. Re-judge the
                // row's current state instead: agreement settles the entry,
                // divergence stays a conflict until the user overwrites.
                return await this.settleAdoptedRow(
                    id,
                    sealed.label,
                    current,
                    overwrite,
                    effects
                );
            }
            // If-Match the version we already hold, NOT the one just listed:
            // re-reading a live token and writing over it is an unacknowledged
            // overwrite, and it would silently revert a rename another session
            // made between our attempts. A stale token 409s into the conflict
            // dialog, which is where that decision belongs. Only an overwrite
            // the user chose in that dialog takes the live token instead.
            let version = existingRef.version;
            if (overwrite) {
                version = current.version;
                effects.push({ kind: 'updateVersion', id, version });
            }
            return await this.syncAdoptedLabel(
                id,
                sealed.label,
                current,
                version,
                effects
            );
        }

        // The ingest endpoint routes purely by the DICOM PatientID inside
        // the files. A mismatched slice must never leave the browser — it
        // would silently create data under ANOTHER patient. Sealed strings:
        // the guard judges the slices that were captured, which are the
        // slices the sealed blobs serialize.
        for (const slicePatientId of sealed.slicePatientIds) {
            if (slicePatientId !== patientRef.dicomPatientId) {
                return {
                    kind: 'failed',
                    entity: 'image',
                    id,
                    message:
                        'The image does not belong to the loaded patient ' +
                        'and cannot be saved.',
                };
            }
        }

        if (!sealed.blobs) {
            // Ref was null at seal time too, so an upload was expected and
            // sealed; reaching here without bytes is bookkeeping corruption,
            // not a user condition. Fail loudly rather than read live.
            return {
                kind: 'failed',
                entity: 'image',
                id,
                message: 'The image series was not sealed for upload.',
            };
        }

        // The upload response carries the rows the batch touched (new and
        // already-present alike), so identity is adopted from the SAME
        // request that persisted the series — there is no follow-up query
        // and no window for one to fail in.
        const uploaded = await this.gateway.uploadInstances(sealed.blobs);
        const reported = uploaded.find(
            (candidate) => candidate.seriesInstanceUid === sealed.seriesUid
        );
        if (reported) {
            if (reported.wasNew) {
                // This request created the row, so its version token is ours:
                // nothing else can have written over it. Sending the label
                // covers the one gap creation leaves — a rename made between
                // a failed earlier attempt that never committed and this one.
                effects.push({
                    kind: 'setRef',
                    id,
                    ref: {
                        datasetId: reported.datasetId,
                        version: reported.version,
                    },
                });
                return await this.syncAdoptedLabel(
                    id,
                    sealed.label,
                    reported,
                    reported.version,
                    effects
                );
            }
            return await this.settleAdoptedRow(
                id,
                sealed.label,
                reported,
                overwrite,
                effects
            );
        }

        // A response without our row (a connector that predates per-dataset
        // reporting returns none): adopt from the patient detail instead.
        // Counts only in the log — never row identities.
        console.warn(
            `Image upload response matched no series row ` +
                `(${uploaded.length} rows returned); adopting from the ` +
                `patient detail.`
        );
        const rows = await this.gateway
            .listPatientImageSeries(patientRef.patientId)
            .catch(() => null);
        const adopted =
            rows?.find(
                (candidate) => candidate.seriesInstanceUid === sealed.seriesUid
            ) ?? null;
        if (!adopted) {
            // No ref is set, so the retry re-runs the (idempotent) upload.
            return {
                kind: 'failed',
                entity: 'image',
                id,
                message:
                    'The image was uploaded but its database identity ' +
                    'could not be confirmed. Save again to retry.',
            };
        }
        // The patient detail cannot say whether this request created the
        // row, so it is treated as pre-existing — the conservative reading.
        return await this.settleAdoptedRow(
            id,
            sealed.label,
            adopted,
            overwrite,
            effects
        );
    }

    /**
     * Settles an entry against a row this session ADOPTED rather than
     * created. The row's live token was never acknowledged here, so it must
     * not become the If-Match of a write — that write would succeed and
     * silently replace another session's rename. Three honest outcomes:
     *
     * - The names agree: nothing needs writing, and the agreement itself
     *   verifies the row state the token stands for, so the ref is stored
     *   acknowledged and the entry is saved.
     * - They diverge and the user chose overwrite: the replacement is an
     *   acknowledged decision, so the label goes out under the live token.
     * - They diverge otherwise: conflict. The ref is stored with the
     *   unacknowledged marker, so every later plain save re-enters this
     *   judgement against the row's then-current state — the marker keeps
     *   the token out of any If-Match until the user decides.
     */
    private async settleAdoptedRow(
        id: ImageId,
        label: string,
        row: PersistedImageSeries,
        overwrite: boolean,
        effects: SaveEffect[]
    ): Promise<SaveOutcome> {
        if (!label || label === row.seriesDescription) {
            effects.push({
                kind: 'setRef',
                id,
                ref: { datasetId: row.datasetId, version: row.version },
            });
            return { kind: 'saved', entity: 'image', id };
        }
        if (overwrite) {
            effects.push({
                kind: 'setRef',
                id,
                ref: { datasetId: row.datasetId, version: row.version },
            });
            return await this.syncAdoptedLabel(
                id,
                label,
                row,
                row.version,
                effects
            );
        }
        effects.push({
            kind: 'setRef',
            id,
            ref: {
                datasetId: row.datasetId,
                version: row.version,
                unacknowledgedVersion: true,
            },
        });
        return {
            kind: 'conflict',
            entity: 'image',
            id,
            reason: 'staleVersion',
        };
    }

    /**
     * Aligns the freshly adopted row's display name with the local one. A
     * failure here is reported as a failed save rather than swallowed: the
     * series IS in the database, but under a name the user did not choose.
     */
    private async syncAdoptedLabel(
        id: ImageId,
        label: string,
        row: PersistedImageSeries,
        ifMatch: ConnectorVersionToken,
        effects: SaveEffect[]
    ): Promise<SaveOutcome> {
        if (!label || label === row.seriesDescription) {
            return { kind: 'saved', entity: 'image', id };
        }

        const { version } = await this.gateway.updateLabel(
            row.datasetId,
            ifMatch,
            label
        );
        effects.push({ kind: 'updateVersion', id, version });
        return { kind: 'saved', entity: 'image', id };
    }

    private async saveModified(
        id: ImageId,
        patientRef: PatientPersistenceRef,
        overwrite: boolean,
        sealed: SealedImage,
        effects: SaveEffect[]
    ): Promise<SaveOutcome> {
        const ref = this.refs.getRef(id);
        if (!ref) {
            // The classifier only reports renames for persisted images; a
            // missing ref here is bookkeeping corruption. Fail loudly.
            return {
                kind: 'failed',
                entity: 'image',
                id,
                message: 'Renamed image has no database identity',
            };
        }
        const label = sealed.label;
        if (!label) {
            return {
                kind: 'failed',
                entity: 'image',
                id,
                message: 'The image series name is empty and cannot be saved.',
            };
        }

        let version = ref.version;
        if (overwrite) {
            // Acknowledged overwrite: re-sending the token that just 409'd
            // would 409 again and reopen the same dialog forever, leaving
            // discard-and-reload as the only escape. Read the row's live
            // version instead of trusting the cached conflict token.
            const rows = await this.gateway.listPatientImageSeries(
                patientRef.patientId
            );
            const current = rows.find(
                (candidate) => candidate.datasetId === ref.datasetId
            );
            if (!current) {
                return {
                    kind: 'failed',
                    entity: 'image',
                    id,
                    message:
                        'The image series no longer exists in the patient ' +
                        'database and cannot be renamed.',
                };
            }
            version = current.version;
            effects.push({ kind: 'updateVersion', id, version });
        }

        const { version: saved } = await this.gateway.updateLabel(
            ref.datasetId,
            version,
            label
        );
        effects.push({ kind: 'updateVersion', id, version: saved });
        return { kind: 'saved', entity: 'image', id };
    }
}
